import { useEffect, useState } from "react";
import TableOfContents from "../components/TableOfContents";
import { ArrowLeftIcon } from "lucide-react";
import MarkdownRenderer from "../components/MarkdownRenderer";
import SASLogo from "../assets/sas/sas-logo-black.png";
import { useLocation, useNavigate, Navigate } from "react-router-dom";

const images = {
	SASLogo,
};

const SAS = () => {
	const [markdownContent, setMarkdownContent] = useState("");
	const navigate = useNavigate();

	const location = useLocation();
	const isAuthenticated =
		(location.state && location.state.isAuthenticated) ||
		(typeof window !== "undefined" &&
			localStorage.getItem("protectedAccess") === "granted");

	useEffect(() => {
		window.scrollTo(0, 0);

		// Only load markdown content if authenticated
		if (isAuthenticated) {
			fetch("/content/sas.md")
				.then((response) => response.text())
				.then((text) => setMarkdownContent(text))
				.catch((error) => {
					console.error("Error loading markdown:", error);
					// Fallback content
					setMarkdownContent(
						"Content loading failed. Please refresh the page."
					);
				});
		}
	}, [isAuthenticated]);

	if (!isAuthenticated) {
		return <Navigate to="/" />;
	}

	return (
		<div className="relative grid w-full min-h-screen bg-[#F5F1E5] grid-cols-1 lg:grid-cols-[200px_1fr_200px] grid-rows-[30px_1fr_30px]">
			{/* Mobile fixed top bar with Back button */}
			<div className="fixed inset-x-0 top-0 z-40 lg:hidden bg-[#F5F1E5] h-14">
				<div
					className="absolute bottom-0 left-0 right-0 h-[2px]"
					style={{
						background: `linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 50%, #BFA99F 50%, #BFA99F 100%)`,
					}}
				/>
				<div className="h-full flex items-center px-4">
					<button
						className="flex items-center font-semibold space-x-2 text-white text-sm px-2 py-1 rounded-full bg-[#66a7f0] shadow"
						onClick={() => navigate("/")}
					>
						<ArrowLeftIcon className="w-3 h-3 transition-transform duration-200" />
						<span>Back</span>
					</button>
				</div>
			</div>
			{/* Top container (was top border line) */}
			<div className="relative hidden lg:block col-span-3 row-start-1 h-[30px] pointer-events-none z-20">
				<div
					className="absolute bottom-0 left-0 right-0 h-[2px] z-20"
					style={{
						background: `linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 50%, #BFA99F 50%, #BFA99F 100%)`,
					}}
				/>
			</div>

			{/* Vertical lines overlay spanning both rows */}
			<div className="pointer-events-none absolute inset-0 z-10 hidden lg:block">
				<div
					className="absolute top-0 bottom-0 left-[200px] w-[2px]"
					style={{
						background: `linear-gradient(to right, #BFA99F 0%, #BFA99F 50%, #FFFFFF 50%, #FFFFFF 100%)`,
					}}
				/>
				<div
					className="absolute top-0 bottom-0 right-[200px] w-[2px]"
					style={{
						background: `linear-gradient(to left, #BFA99F 0%, #BFA99F 50%, #FFFFFF 50%, #FFFFFF 100%)`,
					}}
				/>
			</div>

			{/* Left container (holds TOC and inner right-edge border) */}
			<div className="relative row-start-2 col-start-1 hidden lg:flex flex-col p-2">
				<div className="fixed hidden lg:flex items-start justify-start z-20">
					<TableOfContents
						content={[markdownContent]}
						pageName="SAS"
						accentColor="#66a7f0"
						homeOnly={false}
						darkMode={false}
						inline
					/>
				</div>
			</div>

			{/* Right container (empty, with inner left-edge border) */}
			<div className="relative hidden lg:block row-start-2 col-start-3"></div>

			{/* Center content */}
			<div className="row-start-2 col-start-1 lg:col-start-2 flex flex-col w-full items-center gap-y-8 px-5 sm:px-8 lg:px-14 pt-16 lg:pt-0 bg-white shadow-[0_0_12px_rgba(0,0,0,0.15)]">
				{/* Header Container */}
				<div className="flex flex-col w-full max-w-[1000px] h-fit justify-center items-center mt-0 lg:mt-[30px] gap-2 bg-[#98bdeb] p-4 rounded-2xl border-[#B7B3B0] bg-gradient-to-b from-white/20 via-white/50 to-white/80 shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-accent/20">
					<div className="flex w-full justify-center items-center">
						<div className="flex w-fit mx-auto px-3 py-1 border border-[#928D8B] text-sm text-[#6c6a67] rounded-full">
							Product Design Internship
						</div>
					</div>
					<p className="text-center text-sm text-[#6c6a67]">
						May - August 2024
					</p>
					<h1 className="text-center text-6xl font-semibold tracking-[-0.04em]">
						SAS Institute
					</h1>
					<img
						src={SASLogo}
						alt="hero"
						className="object-contain max-h-[300px] rounded-2xl my-2"
					/>
					<div className="flex w-full max-w-[1000px] justify-center font-mono tracking-tighter text-sm text-[#7E7B78] gap-3 sm:gap-6 flex-wrap overflow-hidden">
						<p>Figma</p>
						<p>Mural</p>
						<p>Jira</p>
						<p>Confluence</p>
					</div>
					<div className="flex w-full max-w-[1000px] justify-center font-mono tracking-tighter text-sm text-[#7E7B78] gap-3 sm:gap-6 flex-wrap overflow-hidden">
						<p>Design Systems</p>
						<p>Component Design</p>
						<p>UX Writing</p>
						<p>User Research</p>
						<p>LLM System Design</p>
					</div>
				</div>
				<div className="body w-full max-w-[1000px] p-4 rounded-3xl">
					<MarkdownRenderer
						markdown={markdownContent}
						images={images}
						className="w-full"
					/>
					<div className="presentation-container w-full flex justify-center mt-12">
						<iframe
							style={{ border: "1px solid rgba(0, 0, 0, 0.1)" }}
							width="100%"
							className="max-w-[1000px] w-full aspect-video"
							src="https://embed.figma.com/slides/UJSKX2IX9SvR6aFY5gqB1B/SAS-Presentation?node-id=13-146&embed-host=share"
							title="SAS Presentation"
							allowFullScreen
						></iframe>
					</div>
				</div>

				<p className="text-sm text-center mb-12 italic">
					Note: This presentation may not be shared publicly due to company
					policy. Thank you for your understanding!
				</p>

				{/* Close center content wrapper */}
			</div>

			{/* Bottom container (mirror of top border line) */}
			<div className="relative col-span-3 row-start-3 h-[30px] pointer-events-none z-20">
				<div
					className="absolute top-0 left-0 right-0 h-[2px] z-20"
					style={{
						background: `linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 50%, #BFA99F 50%, #BFA99F 100%)`,
					}}
				/>
			</div>
		</div>
	);
};

export default SAS;
